import React, { useState, useEffect, useContext, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LanguageContext from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getProducts, getProductById } from '../../services/productService';
import { getCategoryLabel } from '../../services/categoryService';
import { getPortfolioItems, getPortfolioItemById } from '../../services/portfolioService';
import ProductBasicInfo from '../../components/admin/ProductBasicInfo';
import ProductTypeSelector from '../../components/admin/ProductTypeSelector';
import PricingSection from '../../components/admin/PricingSection';
import SpecificationsSection from '../../components/admin/SpecificationsSection';
import TagsEditor from '../../components/admin/TagsEditor';
import FeatureFlagsEditor from '../../components/admin/FeatureFlagsEditor';
import VisibilitySection from '../../components/admin/VisibilitySection';
import ShowcaseDetailsSection from '../../components/admin/ShowcaseDetailsSection';
import ImagesSection from '../../components/admin/ImagesSection';
import DemoEditor from '../../components/admin/DemoEditor';
import SettingsEditor from '../../components/admin/SettingsEditor';
import './AdminPanel.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

const AdminPanel = () => {
  const { language } = useContext(LanguageContext);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('products');
  const [editingCategory, setEditingCategory] = useState(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ label: '', description: '', slug: '', key: '' });
  const [categoryMeta, setCategoryMeta] = useState({});
  const [expandedSections, setExpandedSections] = useState(['basic']);
  const [searchQuery, setSearchQuery] = useState('');
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [editingPortfolioItem, setEditingPortfolioItem] = useState(null);
  const [portfolioFormData, setPortfolioFormData] = useState({});
  const [portfolioLoading, setPortfolioLoading] = useState(true);

  // Load products on component mount
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/products?lang=${language}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
      alert('Failed to load products. Please check if the API server is running.');
      // Fallback to local data
      const localData = getProducts(language);
      setProducts(localData.products || []);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle nested object changes
  const handleNestedChange = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Toggle accordion sections
  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Start editing a product
  const startEditProduct = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}?lang=${language}`);
      if (response.ok) {
        const product = await response.json();
        setEditingProduct(productId);
        setFormData(JSON.parse(JSON.stringify(product))); // Deep copy
        return;
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
    }
    // Fallback to local data
    const product = getProductById(productId, language);
    if (product) {
      setEditingProduct(productId);
      setFormData(JSON.parse(JSON.stringify(product))); // Deep copy
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingProduct(null);
    setFormData({});
  };

  // Save product changes
  const saveProduct = async () => {
    try {
      const isNewProduct = !products.some(p => p.id === editingProduct);
      const url = `${API_BASE_URL}/api/products${isNewProduct ? '' : `/${editingProduct}`}?lang=${language}`;
      const method = isNewProduct ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const savedProduct = await response.json();

      // Update local state
      let updatedProducts;
      if (isNewProduct) {
        updatedProducts = [...products, savedProduct];
      } else {
        updatedProducts = products.map(p =>
          p.id === editingProduct ? savedProduct : p
        );
      }

      setProducts(updatedProducts);
      setEditingProduct(null);
      setFormData({});

      alert(`Product ${isNewProduct ? 'created' : 'updated'} successfully!`);

    } catch (error) {
      console.error('Failed to save product:', error);
      alert(`Failed to save product: ${error.message}. Changes are only in memory.`);

      // Fallback to local update
      const updatedProducts = products.map(p =>
        p.id === editingProduct ? { ...p, ...formData } : p
      );
      setProducts(updatedProducts);
      setEditingProduct(null);
      setFormData({});
    }
  };

  // Delete a product
  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}?lang=${language}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const updatedProducts = products.filter(p => p.id !== productId);
      setProducts(updatedProducts);
      alert('Product deleted successfully!');
    } catch (error) {
      console.error('Failed to delete product:', error);
      alert(`Failed to delete product: ${error.message}. Removing from local state only.`);
      // Fallback to local delete
      const updatedProducts = products.filter(p => p.id !== productId);
      setProducts(updatedProducts);
    }
  };

  // Duplicate a product
  const duplicateProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const clone = JSON.parse(JSON.stringify(product));
    clone.id = `product-${Date.now()}`;
    clone.name = `${clone.name} (Copy)`;
    clone.metadata = {
      ...clone.metadata,
      created: new Date().toISOString().split('T')[0],
      updated: new Date().toISOString().split('T')[0],
      notes: 'Duplicated from ' + productId
    };
    setEditingProduct(clone.id);
    setFormData(clone);
  };

  // Add new product
  const addNewProduct = () => {
    const newProduct = {
      id: `product-${Date.now()}`,
      category: 'business-solutions',
      name: 'New Product',
      description: 'Product description',
      shortDescription: 'Short description',
      demoUrl: '',
      images: [],
      features: [],
      specifications: {},
      visibility: {
        enabled: true,
        showInCatalog: true,
        showOnHomepage: false,
        showInCategory: true,
        showInSearch: true,
        showInRelated: true,
        featured: false,
        showPrice: false,
        showSpecifications: true,
        showGallery: true,
        showDemoLink: false,
        showDownloadLink: false,
        showContactForm: true,
        available: true,
        new: true,
        onSale: false,
        seasonal: false,
        seasonStart: null,
        seasonEnd: null,
        priority: 5
      },
      featureFlags: {
        hasDemo: false,
        hasVideo: false,
        has3DView: false,
        hasSpecSheet: false,
        hasCaseStudy: false,
        hasTestimonials: false,
        hasComparisons: false
      },
      type: 'product',
      subtype: 'software',
      tags: [],
      showcaseDetails: {
        client: '',
        results: '',
        timeline: '',
        testimonial: '',
        beforeAfter: {
          enabled: false,
          beforeImage: '',
          afterImage: ''
        }
      },
      pricing: {
        enabled: false,
        price: 0,
        currency: 'MYR',
        priceType: 'quote',
        subscription: {
          monthly: 0,
          annual: 0
        }
      },
      metadata: {
        created: new Date().toISOString().split('T')[0],
        updated: new Date().toISOString().split('T')[0],
        version: '1.0',
        author: 'admin',
        notes: 'New product'
      }
    };

    setEditingProduct(newProduct.id);
    setFormData(newProduct);
  };

  // Load category metadata
  const loadCategoryMeta = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories?lang=${language}`);
      if (response.ok) {
        const categories = await response.json();
        const meta = {};
        categories.forEach(cat => {
          meta[cat.key] = cat;
        });
        setCategoryMeta(meta);
        return;
      }
    } catch (error) {
      console.error('Failed to load category metadata:', error);
    }
    // Fallback defaults
    const defaults = {
      'business-solutions': { key: 'business-solutions', label: 'Business Solutions', description: '', productCount: 0, activeCount: 0 },
      'automation': { key: 'automation', label: 'Automation', description: '', productCount: 0, activeCount: 0 },
      'labs': { key: 'labs', label: 'Labs', description: '', productCount: 0, activeCount: 0 },
      'education': { key: 'education', label: 'Education', description: '', productCount: 0, activeCount: 0 }
    };
    // Fill in counts from products
    products.forEach(p => {
      if (defaults[p.category]) {
        defaults[p.category].productCount++;
        if (p.visibility.enabled) defaults[p.category].activeCount++;
      }
    });
    setCategoryMeta(defaults);
  }, [language, products]);

  // Load categories when products change
  useEffect(() => {
    loadCategoryMeta();
  }, [loadCategoryMeta]);

  // Start editing a category
  const startEditCategory = (categoryKey) => {
    const meta = categoryMeta[categoryKey];
    setEditingCategory(categoryKey);
    setCategoryFormData({
      label: meta?.label || categoryLabel(categoryKey),
      description: meta?.description || '',
      slug: meta?.slug || '',
      icon: meta?.icon || '',
      enabled: meta?.enabled !== false
    });
  };

  // Start adding a new category
  const startAddCategory = () => {
    setIsAddingCategory(true);
    setCategoryFormData({ key: '', label: '', description: '', slug: '', icon: '', enabled: true });
  };

  // Cancel category editing
  const cancelEditCategory = () => {
    setEditingCategory(null);
    setIsAddingCategory(false);
    setCategoryFormData({ label: '', description: '', slug: '', key: '' });
  };

  // Handle category form changes
  const handleCategoryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Save category changes
  const saveCategory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories/${editingCategory}?lang=${language}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryFormData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || `API error: ${response.status}`;
        throw new Error(errorMsg);
      }

      const updated = await response.json();

      // Update local categoryMeta
      setCategoryMeta(prev => ({
        ...prev,
        [editingCategory]: { ...prev[editingCategory], ...updated }
      }));

      setEditingCategory(null);
      setCategoryFormData({ label: '', description: '' });
      alert('Category updated successfully!');
    } catch (error) {
      console.error('Failed to save category:', error);
      // Apply locally as fallback
      setCategoryMeta(prev => ({
        ...prev,
        [editingCategory]: { ...prev[editingCategory], ...categoryFormData }
      }));
      setEditingCategory(null);
      setCategoryFormData({ label: '', description: '' });
      alert(`Category updated locally: ${error.message}`);
    }
  };

  // Add a new category
  const addCategory = async () => {
    if (!categoryFormData.key || !categoryFormData.key.trim()) {
      alert('Category key is required');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories?lang=${language}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryFormData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || `API error: ${response.status}`;
        throw new Error(errorMsg);
      }

      setIsAddingCategory(false);
      setCategoryFormData({ label: '', description: '', slug: '', key: '' });
      await loadCategoryMeta();
      alert('Category created successfully!');
    } catch (error) {
      console.error('Failed to add category:', error);
      alert(`Failed to create category: ${error.message}`);
    }
  };

  // Delete category metadata
  const deleteCategory = async (categoryKey) => {
    const meta = categoryMeta[categoryKey];
    const productCount = meta?.productCount ?? 0;
    const label = meta?.label || categoryLabel(categoryKey);

    const message = productCount === 0
      ? `Permanently delete category "${label}"? This cannot be undone.`
      : `Permanently delete category "${label}"? ${productCount} product(s) will be unassigned from this category. This cannot be undone.`;

    if (!window.confirm(message)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/categories/${categoryKey}?lang=${language}&hard=true`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || `API error: ${response.status}`;
        throw new Error(errorMsg);
      }

      await loadCategoryMeta();
      alert('Category permanently deleted!');
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert(`Failed to delete category: ${error.message}`);
    }
  };

  // Load portfolio items
  const loadPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/portfolio?lang=${language}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setPortfolioItems(data.portfolio || []);
    } catch (error) {
      console.error('Failed to load portfolio items:', error);
      const localData = getPortfolioItems(language);
      setPortfolioItems(localData.portfolio || []);
    } finally {
      setPortfolioLoading(false);
    }
  }, [language]);

  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  // Start editing a portfolio item
  const startEditPortfolio = async (itemId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/portfolio/${itemId}?lang=${language}`);
      if (response.ok) {
        const item = await response.json();
        setEditingPortfolioItem(itemId);
        setPortfolioFormData(JSON.parse(JSON.stringify(item)));
        return;
      }
    } catch (error) {
      console.error('Failed to fetch portfolio item:', error);
    }
    const item = getPortfolioItemById(itemId, language);
    if (item) {
      setEditingPortfolioItem(itemId);
      setPortfolioFormData(JSON.parse(JSON.stringify(item)));
    }
  };

  // Cancel editing portfolio item
  const cancelEditPortfolio = () => {
    setEditingPortfolioItem(null);
    setPortfolioFormData({});
  };

  // Handle portfolio form input changes
  const handlePortfolioInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPortfolioFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save portfolio item
  const savePortfolio = async () => {
    try {
      const isNewItem = !portfolioItems.some(p => p.id === editingPortfolioItem);
      const url = `${API_BASE_URL}/api/portfolio${isNewItem ? '' : `/${editingPortfolioItem}`}?lang=${language}`;
      const method = isNewItem ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(portfolioFormData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const savedItem = await response.json();

      let updatedItems;
      if (isNewItem) {
        updatedItems = [...portfolioItems, savedItem];
      } else {
        updatedItems = portfolioItems.map(p =>
          p.id === editingPortfolioItem ? savedItem : p
        );
      }

      setPortfolioItems(updatedItems);
      setEditingPortfolioItem(null);
      setPortfolioFormData({});

      alert(`Portfolio item ${isNewItem ? 'created' : 'updated'} successfully!`);
    } catch (error) {
      console.error('Failed to save portfolio item:', error);
      alert(`Failed to save portfolio item: ${error.message}. Changes are only in memory.`);
      const updatedItems = portfolioItems.map(p =>
        p.id === editingPortfolioItem ? { ...p, ...portfolioFormData } : p
      );
      setPortfolioItems(updatedItems);
      setEditingPortfolioItem(null);
      setPortfolioFormData({});
    }
  };

  // Delete a portfolio item
  const deletePortfolioItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/portfolio/${itemId}?lang=${language}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const updatedItems = portfolioItems.filter(p => p.id !== itemId);
      setPortfolioItems(updatedItems);
      alert('Portfolio item deleted successfully!');
    } catch (error) {
      console.error('Failed to delete portfolio item:', error);
      alert(`Failed to delete portfolio item: ${error.message}. Removing from local state only.`);
      const updatedItems = portfolioItems.filter(p => p.id !== itemId);
      setPortfolioItems(updatedItems);
    }
  };

  // Add new portfolio item
  const addNewPortfolio = () => {
    const existingOrders = portfolioItems
      .map(p => p.order || 0)
      .filter(o => typeof o === 'number');
    const nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

    const newItem = {
      id: `portfolio-${Date.now()}`,
      title: 'New Portfolio Item',
      description: 'Project description',
      category: 'Business Solutions',
      results: 'Key results and metrics',
      image: '',
      order: nextOrder,
      enabled: true
    };

    setEditingPortfolioItem(newItem.id);
    setPortfolioFormData(newItem);
  };

  // Upload image for portfolio
  const handlePortfolioImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
      const data = await response.json();
      setPortfolioFormData(prev => ({ ...prev, image: data.url }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image: ' + error.message);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  const categoryLabel = (cat) => getCategoryLabel(cat, language);

  return (
    <motion.div
      className="admin-panel"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title">
            <h1 className="page-title">Admin Panel</h1>
            <p className="admin-subtitle">Manage products, categories, and settings</p>
          </div>
          <button className="btn btn-outline btn-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={`tab ${activeTab === 'portfolio' ? 'active' : ''}`}
          onClick={() => setActiveTab('portfolio')}
        >
          Portfolio
        </button>
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <motion.div className="tab-content" variants={itemVariants}>
          <div className="admin-actions">
            <button className="btn btn-primary" onClick={addNewProduct}>
              + Add New Product
            </button>
            <button className="btn btn-outline" onClick={loadProducts}>
              ↻ Refresh
            </button>
          </div>

          <div className="admin-search">
            <input
              type="text"
              className="form-control"
              placeholder="Search products by name, category, or tags..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading">Loading products...</div>
          ) : (
            <div className="products-list">
              {products
                .filter(product => {
                  if (!searchQuery.trim()) return true;
                  const q = searchQuery.toLowerCase();
                  return (
                    product.name?.toLowerCase().includes(q) ||
                    product.category?.toLowerCase().includes(q) ||
                    product.shortDescription?.toLowerCase().includes(q) ||
                    product.tags?.some(t => t.toLowerCase().includes(q))
                  );
                })
                .map((product) => (
                <div key={product.id} className="admin-product-card">
                  {product.images && product.images[0] && (
                    <div className="product-thumbnail">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="product-header">
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <div className="product-meta">
                        <span className="category">{categoryLabel(product.category)}</span>
                        <span className="type">{product.type}</span>
                        <span className={`status ${product.visibility.enabled ? 'active' : 'inactive'}`}>
                          {product.visibility.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="product-actions">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => startEditProduct(product.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => duplicateProduct(product.id)}
                      >
                        Duplicate
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteProduct(product.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="product-details">
                    <p className="description">{product.shortDescription}</p>
                    <div className="product-stats">
                      <span>ID: {product.id}</span>
                      <span>Images: {product.images?.length || 0}</span>
                      <span>Features: {product.features?.length || 0}</span>
                      {product.visibility.featured && <span className="featured">Featured</span>}
                      {product.visibility.new && <span className="new">New</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <motion.div className="tab-content" variants={itemVariants}>
          <div className="categories-section">
            <div className="section-header">
              <div>
                <h2>Product Categories</h2>
                <p>Manage product categories and their display settings.</p>
              </div>
              <button className="btn btn-primary" onClick={startAddCategory}>
                + Add Category
              </button>
            </div>

            <div className="categories-list">
              {Object.keys(categoryMeta).length > 0
                ? Object.keys(categoryMeta)
                    .filter(category => categoryMeta[category]?.enabled !== false)
                    .map((category) => {
                const categoryProducts = products.filter(p => p.category === category);
                const meta = categoryMeta[category] || {};
                return (
                  <div key={category} className="category-card">
                    <h3>{meta.label || categoryLabel(category)}</h3>
                    <p className="category-description">{meta.description || ''}</p>
                    <div className="category-stats">
                      <span>Products: {categoryProducts.length}</span>
                      <span>Active: {categoryProducts.filter(p => p.visibility.enabled).length}</span>
                    </div>
                    <div className="category-actions">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => startEditCategory(category)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteCategory(category)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
                : null
              }
            </div>
          </div>
        </motion.div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <motion.div className="tab-content" variants={itemVariants}>
          <div className="settings-section">
            <h2>System Settings</h2>
            <p>Configure global feature toggles and display preferences.</p>
            <SettingsEditor API_BASE_URL={API_BASE_URL} language={language} />
          </div>
        </motion.div>
      )}

      {/* Portfolio Tab */}
      {activeTab === 'portfolio' && (
        <motion.div className="tab-content" variants={itemVariants}>
          <div className="admin-actions">
            <button className="btn btn-primary" onClick={addNewPortfolio}>
              + Add Portfolio Item
            </button>
            <button className="btn btn-outline" onClick={loadPortfolio}>
              ↻ Refresh
            </button>
          </div>

          {portfolioLoading ? (
            <div className="loading">Loading portfolio items...</div>
          ) : (
            <div className="products-list">
              {portfolioItems
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((item) => (
                <div key={item.id} className="admin-product-card">
                  {item.image && (
                    <div className="product-thumbnail">
                      <img
                        src={item.image}
                        alt={item.title}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="product-header">
                    <div className="product-info">
                      <h3>{item.title}</h3>
                      <div className="product-meta">
                        <span className="category">{item.category}</span>
                        <span className={`status ${item.enabled !== false ? 'active' : 'inactive'}`}>
                          {item.enabled !== false ? 'Active' : 'Inactive'}
                        </span>
                        <span className="type">Order: {item.order}</span>
                      </div>
                    </div>
                    <div className="product-actions">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => startEditPortfolio(item.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deletePortfolioItem(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="product-details">
                    <p className="description">{item.description}</p>
                    <div className="product-stats">
                      <span>ID: {item.id}</span>
                      <span>Results: {item.results}</span>
                    </div>
                  </div>
                </div>
              ))}
              {portfolioItems.length === 0 && (
                <p className="empty-message">No portfolio items yet. Click "+ Add Portfolio Item" to create one.</p>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="modal-overlay">
          <motion.div className="modal" variants={itemVariants}>
            <div className="modal-header">
              <h2>{formData.id === editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="close-btn" onClick={cancelEdit}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Basic Info - always expanded */}
              <div className={`accordion-section ${expandedSections.includes('basic') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('basic')}>
                  <h3>Basic Information</h3>
                  <span className={`accordion-icon ${expandedSections.includes('basic') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('basic') && (
                  <div className="accordion-body">
                    <ProductBasicInfo formData={formData} handleInputChange={handleInputChange} categories={Object.keys(categoryMeta).map(k => ({ value: k, label: categoryMeta[k]?.label || k }))} />
                    <ProductTypeSelector formData={formData} handleInputChange={handleInputChange} />
                  </div>
                )}
              </div>

              {/* Images */}
              <div className={`accordion-section ${expandedSections.includes('images') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('images')}>
                  <h3>Images</h3>
                  <span className={`accordion-icon ${expandedSections.includes('images') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('images') && (
                  <div className="accordion-body">
                    <ImagesSection formData={formData} setFormData={setFormData} API_BASE_URL={API_BASE_URL} />
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className={`accordion-section ${expandedSections.includes('pricing') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('pricing')}>
                  <h3>Pricing</h3>
                  <span className={`accordion-icon ${expandedSections.includes('pricing') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('pricing') && (
                  <div className="accordion-body">
                    <PricingSection formData={formData} handleNestedChange={handleNestedChange} />
                  </div>
                )}
              </div>

              {/* Specifications */}
              <div className={`accordion-section ${expandedSections.includes('specs') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('specs')}>
                  <h3>Specifications</h3>
                  <span className={`accordion-icon ${expandedSections.includes('specs') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('specs') && (
                  <div className="accordion-body">
                    <SpecificationsSection formData={formData} setFormData={setFormData} />
                  </div>
                )}
              </div>

              {/* Features */}
              <div className={`accordion-section ${expandedSections.includes('features') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('features')}>
                  <h3>Features</h3>
                  <span className={`accordion-icon ${expandedSections.includes('features') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('features') && (
                  <div className="accordion-body">
                    <div className="form-group">
                      <label>Feature List (comma separated)</label>
                      <input
                        type="text"
                        value={(formData.features || []).join(', ')}
                        onChange={(e) => {
                          const features = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                          setFormData(prev => ({ ...prev, features }));
                        }}
                        className="form-control"
                        placeholder="Feature 1, Feature 2, Feature 3"
                      />
                    </div>
                    <FeatureFlagsEditor formData={formData} setFormData={setFormData} />
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className={`accordion-section ${expandedSections.includes('tags') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('tags')}>
                  <h3>Tags</h3>
                  <span className={`accordion-icon ${expandedSections.includes('tags') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('tags') && (
                  <div className="accordion-body">
                    <TagsEditor formData={formData} setFormData={setFormData} />
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div className={`accordion-section ${expandedSections.includes('visibility') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('visibility')}>
                  <h3>Visibility</h3>
                  <span className={`accordion-icon ${expandedSections.includes('visibility') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('visibility') && (
                  <div className="accordion-body">
                    <VisibilitySection formData={formData} handleNestedChange={handleNestedChange} />
                  </div>
                )}
              </div>

              {/* Demo Link */}
              <div className={`accordion-section ${expandedSections.includes('demo') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('demo')}>
                  <h3>Demo Link</h3>
                  <span className={`accordion-icon ${expandedSections.includes('demo') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('demo') && (
                  <div className="accordion-body">
                    <DemoEditor formData={formData} handleInputChange={handleInputChange} />
                  </div>
                )}
              </div>

              {/* Showcase Details */}
              <div className={`accordion-section ${expandedSections.includes('showcase') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('showcase')}>
                  <h3>Showcase Details</h3>
                  <span className={`accordion-icon ${expandedSections.includes('showcase') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('showcase') && (
                  <div className="accordion-body">
                    <ShowcaseDetailsSection formData={formData} handleNestedChange={handleNestedChange} API_BASE_URL={API_BASE_URL} />
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div className={`accordion-section ${expandedSections.includes('metadata') ? 'expanded' : ''}`}>
                <div className="accordion-header" onClick={() => toggleSection('metadata')}>
                  <h3>Metadata</h3>
                  <span className={`accordion-icon ${expandedSections.includes('metadata') ? 'open' : ''}`}>▶</span>
                </div>
                {expandedSections.includes('metadata') && (
                  <div className="accordion-body">
                    <div className="form-section">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Created</label>
                          <input type="text" className="form-control" value={formData.metadata?.created || ''} readOnly />
                        </div>
                        <div className="form-group">
                          <label>Updated</label>
                          <input type="text" className="form-control" value={formData.metadata?.updated || ''} readOnly />
                        </div>
                        <div className="form-group">
                          <label>Version</label>
                          <input type="text" className="form-control" value={formData.metadata?.version || ''} readOnly />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Author</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.metadata?.author || ''}
                            onChange={(e) => handleNestedChange('metadata.author', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Notes</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.metadata?.notes || ''}
                            onChange={(e) => handleNestedChange('metadata.notes', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={cancelEdit}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveProduct}>
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit / Add Category Modal */}
      {(editingCategory || isAddingCategory) && (
        <div className="modal-overlay">
          <motion.div className="modal modal-sm" variants={itemVariants}>
            <div className="modal-header">
              <h2>{isAddingCategory ? 'Add Category' : `Edit Category: ${categoryFormData.label}`}</h2>
              <button className="close-btn" onClick={cancelEditCategory}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <h3>Category Settings</h3>
                {isAddingCategory && (
                  <div className="form-group">
                    <label>Category Key *</label>
                    <input
                      type="text"
                      name="key"
                      value={categoryFormData.key || ''}
                      onChange={handleCategoryInputChange}
                      className="form-control"
                      placeholder="e.g. cloud-services"
                    />
                    <small>Unique identifier used in URLs: /services/<strong>{categoryFormData.key || 'key'}</strong></small>
                  </div>
                )}
                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    name="label"
                    value={categoryFormData.label || ''}
                    onChange={handleCategoryInputChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>URL Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={categoryFormData.slug || ''}
                    onChange={handleCategoryInputChange}
                    className="form-control"
                    placeholder="e.g. automation (leave empty to use key)"
                  />
                  <small>Used in the URL: /services/<strong>{categoryFormData.slug || editingCategory || categoryFormData.key || 'key'}</strong></small>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={categoryFormData.description || ''}
                    onChange={handleCategoryInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Brief description of this category"
                  />
                </div>
                <div className="form-group">
                  <label>Icon (CSS class or emoji)</label>
                  <input
                    type="text"
                    name="icon"
                    value={categoryFormData.icon || ''}
                    onChange={handleCategoryInputChange}
                    className="form-control"
                    placeholder="e.g. bi-briefcase, 🏭"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={categoryFormData.enabled !== false}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    Category Enabled (show on website)
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={cancelEditCategory}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={isAddingCategory ? addCategory : saveCategory}>
                {isAddingCategory ? 'Create Category' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit / Add Portfolio Item Modal */}
      {editingPortfolioItem && (
        <div className="modal-overlay">
          <motion.div className="modal modal-sm" variants={itemVariants}>
            <div className="modal-header">
              <h2>{portfolioItems.some(p => p.id === editingPortfolioItem) ? 'Edit Portfolio Item' : 'Add Portfolio Item'}</h2>
              <button className="close-btn" onClick={cancelEditPortfolio}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <h3>Portfolio Item Details</h3>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={portfolioFormData.title || ''}
                    onChange={handlePortfolioInputChange}
                    className="form-control"
                    placeholder="Project title"
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <input
                    type="text"
                    name="category"
                    value={portfolioFormData.category || ''}
                    onChange={handlePortfolioInputChange}
                    className="form-control"
                    placeholder="e.g. Business Solutions"
                  />
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    value={portfolioFormData.description || ''}
                    onChange={handlePortfolioInputChange}
                    className="form-control"
                    rows="4"
                    placeholder="Project description"
                  />
                </div>
                <div className="form-group">
                  <label>Results *</label>
                  <textarea
                    name="results"
                    value={portfolioFormData.results || ''}
                    onChange={handlePortfolioInputChange}
                    className="form-control"
                    rows="4"
                    placeholder="e.g. 40% efficiency improvement, RM50k annual savings"
                  />
                </div>
                <div className="form-group">
                  <label>Image</label>
                  <div className="image-input-group">
                    <input
                      type="text"
                      name="image"
                      value={portfolioFormData.image || ''}
                      onChange={handlePortfolioInputChange}
                      className="form-control"
                      placeholder="Image URL or upload below"
                    />
                    <label className="btn btn-sm btn-outline upload-btn">
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePortfolioImageUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {portfolioFormData.image && (
                    <div className="image-preview">
                      <img src={portfolioFormData.image} alt="Preview" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      name="order"
                      value={portfolioFormData.order || 0}
                      onChange={(e) => setPortfolioFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label" style={{ marginTop: '1.8rem' }}>
                      <input
                        type="checkbox"
                        name="enabled"
                        checked={portfolioFormData.enabled !== false}
                        onChange={handlePortfolioInputChange}
                      />
                      Enabled (show on website)
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={cancelEditPortfolio}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={savePortfolio}>
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminPanel;